'use client'

// libraries
import { useRef, useState, useEffect } from 'react'
import clsx from 'clsx'

// components
import Image from '/react4xp/components/Image'
import AudioToggle from '/react4xp/components/AudioToggle'

// svg
import { Play } from '/react4xp/components/Icons/Ux'

// types
export interface VideoProps {
    className?: string
    url: string
    type: 'youtube' | 'vimeo' | 'default'
    background?: boolean
    aspectRatio?: '16:9' | '9:16' | 'custom'
    image?: {
        src?: string
        srcMedium?: string
        srcSmall?: string
        alt?: string
    }
    caption?: string
}

// css
import './index.scss'

export default function Video({
    className,
    url,
    type,
    background = false,
    aspectRatio,
    image,
    caption
}: VideoProps) {

    const videoRef = useRef<HTMLVideoElement>(null)
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(background)
    const [isPaused, setIsPaused] = useState(!background)

    const handlePlay = () => {
        if (type === 'default') {
            if (videoRef.current) {
                videoRef.current.play()
            }
        } else if (type === 'youtube') {
            iframeRef.current?.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*')
        } else if (type === 'vimeo') {
            iframeRef.current?.contentWindow?.postMessage('{"method":"play"}', '*')
        }
        setIsPlaying(true)
        setIsPaused(false)
    }

    const toggleMute = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (type === 'default' && videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted
            setIsMuted(videoRef.current.muted)
        } else if (type === 'youtube' && iframeRef.current) {
            const newMutedState = !isMuted
            iframeRef.current.contentWindow?.postMessage(
                JSON.stringify({
                    event: 'command',
                    func: newMutedState ? 'mute' : 'unMute',
                    args: ''
                }),
                '*'
            )
            setIsMuted(newMutedState)
        } else if (type === 'vimeo' && iframeRef.current) {
            const newMutedState = !isMuted
            // Use setMuted method for Vimeo
            iframeRef.current.contentWindow?.postMessage(
                JSON.stringify({
                    method: 'setMuted',
                    value: newMutedState
                }),
                '*'
            )
            setIsMuted(newMutedState)
        }
    }

    const getYoutubeId = (input: string) => {
        const patterns = [
            /(?:youtube\.com\/(?:.*v=|v\/|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/,
            /([\w-]{11})/
        ]
        for (const pattern of patterns) {
            const match = input.match(pattern)
            if (match && match[1]) {
                return match[1]
            }
        }
        return ''
    }

    const youtubeId = type === 'youtube' ? getYoutubeId(url) : ''

    const isBg = background
    const youtubeMutedParam = isBg ? 1 : (isMuted ? 1 : 0)
    const isAutoplay = isBg ? 1 : 0
    const isControls = isBg ? 0 : 1
    const isLoop = isBg ? 1 : 0
    const youtubeParams = [
        `autoplay=${isAutoplay}`,
        'playsinline=1',
        `controls=${isControls}`,
        `mute=${youtubeMutedParam}`,
        `loop=${isLoop}`,
        'modestbranding=1',
        'rel=0',
        'iv_load_policy=3',
        'showinfo=0',
        'enablejsapi=1'
    ]
    if (isLoop) {
        youtubeParams.push(`playlist=${youtubeId}`)
    }

    // Track play/pause state for native videos
    useEffect(() => {
        if (type !== 'default' || !videoRef.current) return

        const videoElement = videoRef.current

        const handlePlay = () => {
            setIsPaused(false)
            setIsPlaying(true)
        }
        const handlePause = () => setIsPaused(true)

        videoElement.addEventListener('play', handlePlay)
        videoElement.addEventListener('pause', handlePause)

        return () => {
            videoElement.removeEventListener('play', handlePlay)
            videoElement.removeEventListener('pause', handlePause)
        }
    }, [type])

    // Initialize muted state for native videos
    useEffect(() => {
        if (type === 'default' && videoRef.current) {
            videoRef.current.muted = isMuted
        }
    }, [type, isMuted])

    return (
        <>
            <div className={clsx(
                'video-component',
                isBg && 'video-component--no-click',
                aspectRatio && aspectRatio === '16:9' && 'video-component--16-9',
                aspectRatio && aspectRatio === '9:16' && 'video-component--9-16',
                aspectRatio && aspectRatio === 'custom' && 'video-component--custom',
                className
            )}>

                {isBg && (
                    <AudioToggle
                        isMuted={isMuted}
                        isPaused={isPaused}
                        onClick={toggleMute}
                    />
                )}

                {!isBg && (
                    <button
                        className={clsx(
                            'video-component__overlay',
                            isPlaying && 'video-component__overlay--hidden'
                        )}
                        onClick={handlePlay}
                    >
                        
                        <span className='video-component__overlay__icon'>
                            <Play />
                        </span>

                        {image && (
                            <Image
                                src={{
                                    src: image.src || '',
                                    width: 1320,
                                    height: 750
                                }}
                                srcMedium={{
                                    src: image.srcMedium || '',
                                    width: 750,
                                    height: 420
                                }}
                                srcSmall={{
                                    src: image.srcSmall || '',
                                    width: 500,
                                    height: 280
                                }}
                                alt={image.alt || ''}
                                sizes='100vw'
                                lazyLoad
                                className='cover'
                            />
                        )}

                    </button>
                )}

                {type === 'default' && (
                    <video
                        ref={videoRef}
                        className='cover'
                        loop
                        controls={!isBg}
                        autoPlay={isBg}
                        muted={isMuted}
                        playsInline
                        onLoadedMetadata={() => {
                            if (videoRef.current) {
                                setIsMuted(videoRef.current.muted)
                            }
                        }}
                    >
                        <source src={url + '#t=0.001'} type='video/mp4' />
                    </video>
                )}

                {type === 'youtube' && (
                    <iframe
                        ref={iframeRef}
                        className='cover'
                        src={`https://www.youtube-nocookie.com/embed/${youtubeId}?${youtubeParams.join('&')}`}
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                        allowFullScreen
                        tabIndex={!isBg ? 0 : -1}
                        loading='lazy'
                    />
                )}

                {type === 'vimeo' && (
                    <iframe
                        key={`vimeo-${url.split('/').pop()}`}
                        ref={iframeRef}
                        className='cover'
                        src={`https://player.vimeo.com/video/${url.split('/').pop()}?title=0&byline=0&portrait=0&controls=${isBg ? 0 : 1}${isBg ? '&autoplay=1&muted=1&loop=1' : ''}`}
                        allow='autoplay; fullscreen; picture-in-picture'
                        allowFullScreen
                        tabIndex={!isBg ? 0 : -1}
                        loading='lazy'
                    />
                )}

            </div>

            {caption && (
                <p className='video-component__caption body-small'>
                    {caption}
                </p>
            )}

        </>
    )
}